import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../../App';

const STATE_COPY = {
  quota: { label: 'out of credits', cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  failing: { label: 'key rejected', cls: 'bg-rose-100 text-rose-700 border-rose-300' },
  missing: { label: 'no key', cls: 'bg-slate-200 text-slate-700 border-slate-300' },
  unreachable: { label: 'unreachable', cls: 'bg-sky-100 text-sky-800 border-sky-300' },
};

export default function KeyHealthBanner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('key_health_dismissed') === '1');

  useEffect(() => {
    if (user?.role !== 'admin') return;
    api.get('/api/system/key-health').then(r => setHealth(r.data)).catch(() => {});
  }, [user]);

  if (user?.role !== 'admin' || dismissed || !health) return null;
  const degraded = health.degraded || [];
  if (!degraded.length) return null;

  const critical = (health.degraded_critical || []).length;
  const close = () => { sessionStorage.setItem('key_health_dismissed', '1'); setDismissed(true); };
  const checked = health.checked_at ? new Date(health.checked_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : null;

  return (
    <div className="mb-5 rounded-2xl border overflow-hidden" style={{ borderColor: critical ? '#FCA5A5' : '#FCD34D', background: critical ? '#FFF1F2' : '#FFFBEB' }} data-testid="key-health-banner">
      <div className="p-4 flex items-start gap-3">
        <span className="mt-0.5 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: critical ? '#E11D48' : '#FAAB1C', color: '#fff' }}>
          <i className="fa-solid fa-heart-pulse text-sm" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-text-primary" data-testid="key-health-headline">
            {degraded.length} data {degraded.length === 1 ? 'source is' : 'sources are'} degraded
            {critical > 0 && <span className="ml-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white">{critical} CRITICAL</span>}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="key-health-chips">
            {degraded.map(p => {
              const s = STATE_COPY[p.state] || STATE_COPY.failing;
              return (
                <span key={p.key} className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${s.cls}`} title={p.detail} data-testid={`key-health-chip-${p.key}`}>
                  {p.label} — {s.label}
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-text-secondary mt-2">
            Affected features fall back to estimated data until the key is fixed.{checked ? ` Last checked ${checked}.` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {user?.is_super && (
            <button onClick={() => navigate('/settings')} className="px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: '#0C2417', color: '#fff' }} data-testid="key-health-fix-btn">
              Fix keys
            </button>
          )}
          <button onClick={close} className="w-7 h-7 rounded-full hover:bg-black/5 text-text-muted" data-testid="key-health-dismiss-btn" aria-label="Dismiss">
            <i className="fa-solid fa-xmark text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
}
